import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import useParemeterLessApi from "../../hooks/useParemeterLessApi";
import useApi from "../../hooks/useApi";
import { useToaster } from "../../hooks/useToaster";
import { useMenu } from "../../context/MenuContext";
import { useUser } from "../../context/UserContext";
import { ModuleNames, POSTIF_STATIC_UI_BYPASS, SEARCHBOX, SEVERITIES } from "../../data/constants";
import { POSTIF_TEXT } from "../../constants/messages";
import SearchBox from "../../components/form/SearchBox/SearchBox";
import { getMyPostInterviewForms, getPostInterviewForms, markPostIfAsEmployee, softDeletePostIf } from "../../services/pifService";
import { getPifDepartments } from "../../services/pifListApproval";
import { PifListItem, PifListStatus } from "../../pages/supervision/PostInterviewForm/types";
import ConfirmationDialog from "../../components/ui/ConfirmationDialog";
import PostIFMarkEmployeeDialog from "./PostIFMarkEmployeeDialog";
import PostIFTable, { PostIFTableVariant } from "./PostIFTable";
import PostIFDetailDialog from "./PostIFDetailDialog";
import PifPageBand from "../../pages/supervision/PostInterviewForm/PifPageBand";
import { PifMenuSelect } from "../../pages/supervision/PostInterviewForm/pifSelectMenu";
import "./PostInterviewForms.scss";

const ALL = "all";
const NEW_FORM_URL = "/hiring/postif/new";

// Every status a list row can hold, in workflow order (DRAFT → pending stages →
// APPROVED/DECLINED). This IS the status filter's option list - it must NOT be derived from the
// loaded rows, or a status that currently has no record would be missing from the dropdown.
const STATUS_ORDER: PifListStatus[] = [
  "DRAFT",
  "RETURNED",
  "PENDING DEPT DIR",
  "PENDING HR DIR",
  "PENDING EXEC DIR",
  "APPROVED",
  "DECLINED",
];

// Permissions decide the grid in a real session, but the static-UI bypass leaves them empty,
// so "?view=hr" / "?view=my" lets both grids be reviewed before the modules are seeded.
const readVariantOverride = (value: string | null): PostIFTableVariant | null => {
  if (!POSTIF_STATIC_UI_BYPASS) return null;
  return value === "hr" || value === "my" ? value : null;
};

const PostInterviewForms: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { permissions } = useMenu();
  const { userId } = useUser();
  const { parameterlessapiloading: loading, parameterlessexecuteApi: executeApi } =
    useParemeterLessApi();
  const { executeApi: executeMutation } = useApi();
  const showToaster = useToaster();
  const [allRows, setAllRows] = useState<PifListItem[]>([]);
  const [departmentLookup, setDepartmentLookup] = useState<string[]>([]);
  const [searchText, setSearchText] = useState("");
  const [department, setDepartment] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  const [detailRow, setDetailRow] = useState<PifListItem | null>(null);
  const [deleteRow, setDeleteRow] = useState<PifListItem | null>(null);
  const [markRow, setMarkRow] = useState<PifListItem | null>(null);

  const canViewHrList = Boolean(
    permissions.find((permission) => permission.moduleName === ModuleNames.PostIFList)?.canView,
  );
  // Hiring supervisor view unless the user has the HR list grant. Empty permissions
  // (static-UI bypass) fall through to the supervisor grid so local work still has a table.
  const variant: PostIFTableVariant =
    readVariantOverride(searchParams.get("view")) ?? (canViewHrList ? "hr" : "my");

  // "+ New PostIF" opens /hiring/postif/new, which MenuGuard gates on
  // EnterprisePortal-PostIF-Form (RouteKeywords "postif/" -> ModuleNames.PostIFForm). Gate the
  // button on that SAME grant rather than on which grid is showing: keyed off the grid, someone
  // with the Form grant but viewing the HR list got no button at all, while a supervisor without
  // it got a button that only ever led to the not-authorized page. The Form module is deliberately
  // separate from the two list grants (see Epic-79767_Supervision_PostIF.sql) precisely so that
  // "who may fill a form" is decided independently of "whose list you can see".
  const formGrant = permissions.find((permission) => permission.moduleName === ModuleNames.PostIFForm);
  const canCreateForm =
    // canView AND canEdit, both on the Form module. canView alone is not enough: creating a form is
    // a WRITE, and in this schema CanEdit is the create flag - Portal_ModuleRolePermissions has no
    // CanAdd column at all, which is why sProc_GetActiveRoles_Module_Permission_S resolves
    // 'CanAdd' as `PMR.CanEdit = 1`. canView is still required because MenuGuard gates the
    // /hiring/postif/new route on it, so without it the button would only lead to Not Authorized.
    Boolean(formGrant?.canView && formGrant?.canEdit) ||
    // The static-UI bypass leaves permissions empty; keep the button reachable for local review,
    // same reasoning as the variant fallback above.
    (POSTIF_STATIC_UI_BYPASS && permissions.length === 0);

  // "My" and "HR" are separate scoped endpoints (my-list / hr-list); the server decides the
  // scope, so we call the one matching the grid variant rather than filtering client-side.
  const loadList = useCallback(async () => {
    const fetchList =
      variant === "hr" ? () => getPostInterviewForms(userId) : () => getMyPostInterviewForms(userId);
    const data = await executeApi(fetchList);
    if (data) {
      setAllRows(data);
    }
  }, [executeApi, variant, userId]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  // The department options come from the /postif/departments master list, NOT from the loaded
  // rows - a department with no form yet must still be selectable. This is deliberately outside
  // useParemeterLessApi: that hook owns the grid's spinner, and a lookup call must not flip the
  // table into a loading state. A lookup failure is swallowed so the page keeps working; the
  // row-derived names below still populate the dropdown.
  useEffect(() => {
    let cancelled = false;
    void getPifDepartments()
      .then((items) => {
        if (!cancelled) setDepartmentLookup(items.map((item) => item.text ?? "").filter(Boolean));
      })
      .catch(() => {
        if (!cancelled) setDepartmentLookup([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Master list unioned with whatever the rows carry, so a department that was renamed or
  // deactivated but still has records stays filterable. The filter matches on NAME
  // (PifListItem.department is the name), which is why the lookup's id is dropped.
  const departments = useMemo(() => {
    const names = new Set(
      [...departmentLookup, ...allRows.map((row) => row.department)].filter(Boolean),
    );
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [departmentLookup, allRows]);

  const visibleRows = useMemo(() => {
    const search = searchText.trim().toLowerCase();

    return allRows.filter((row) => {
      if (department !== ALL && row.department !== department) return false;
      if (status !== ALL && row.status !== status) return false;
      if (!search) return true;

      return [row.title, row.applicant, row.position].some((field) =>
        field.toLowerCase().includes(search),
      );
    });
  }, [allRows, department, status, searchText]);

  const handleNewForm = () => navigate(NEW_FORM_URL);

  // Carry the ?view= override onto the detail routes so the list crumb can return to the
  // same grid — without it the list falls back to permissions and shows the supervisor view.
  const listSearch = searchParams.toString();
  const withListSearch = (path: string) => (listSearch ? `${path}?${listSearch}` : path);

  const handleOpenFromDetail = (row: PifListItem) => {
    setDetailRow(null);
    // "Open form" only OPENS the record - it never starts an edit, on either grid. The plain
    // /:id route is the read-only approval screen (sign buttons appear only when it's the
    // viewer's turn), so a draft or a returned form is shown as-is rather than reopened in the
    // editor. Editing stays on the pencil action in PostIFTable, which is already gated to the
    // rows the viewer may actually mutate; routing an editable row here too meant one click from
    // the lightbox silently put the form back into edit mode.
    navigate(withListSearch(`/hiring/postif/${row.id}`));
  };

  // HR clicks "Mark as employee" → collect the Enterprise employee number, then call the API and
  // refetch (mark-as-employee stamps EnterpriseEmployeeId, which drops the row off the HR list).
  const handleConfirmMark = async (enterpriseEmployeeId: number) => {
    if (!markRow) return;
    const result = await executeMutation(markPostIfAsEmployee, {
      postIfId: Number(markRow.id),
      enterpriseEmployeeId,
    });
    if (result) {
      showToaster("Marked as employee.", SEVERITIES.SUCCESS);
      await loadList();
    }
    setMarkRow(null);
  };

  const handleConfirmDelete = async (confirmed: boolean) => {
    if (confirmed && deleteRow) {
      // useApi surfaces any business failure (403 not-owner, 409 already-submitted, …) as a
      // Redux error alert and returns null, so a non-null result means the delete succeeded.
      const result = await executeMutation(softDeletePostIf, Number(deleteRow.id));
      if (result) {
        showToaster("Draft deleted.", SEVERITIES.SUCCESS);
        await loadList();
      }
    }
    setDeleteRow(null);
  };

  const handleDepartmentChange = (event: SelectChangeEvent<string>) => {
    setDepartment(event.target.value);
  };

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    setStatus(event.target.value);
  };

  return (
    <div className="postif-page">
      {/* No `current`: this page IS the list, so its own crumb ends the trail. */}
      <PifPageBand title="Post Interview Forms" />

      <section className="postif-card">
        <div className="postif-card__toolbar">
          <Box className="search-box-wrap postif-card__search-wrap" data-testid="postif-search">
            <SearchBox
              searchPlaceholder={SEARCHBOX.TEXT_PLACEHOLDER}
              onSearch={setSearchText}
            />
          </Box>

          <FormControl className="postif-card__select" size="small">
            <InputLabel id="postif-department-label">Department</InputLabel>
            <PifMenuSelect
              labelId="postif-department-label"
              id="postif-department-select"
              label="Department"
              value={department}
              onChange={handleDepartmentChange}
              data-testid="postif-filter-dept"
            >
              <MenuItem value={ALL}>All departments</MenuItem>
              {departments.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </PifMenuSelect>
          </FormControl>

          <FormControl className="postif-card__select" size="small">
            <InputLabel id="postif-status-label">Status</InputLabel>
            <PifMenuSelect
              labelId="postif-status-label"
              id="postif-status-select"
              label="Status"
              value={status}
              onChange={handleStatusChange}
              data-testid="postif-filter-status"
            >
              <MenuItem value={ALL}>All statuses</MenuItem>
              {STATUS_ORDER.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </PifMenuSelect>
          </FormControl>

          {canCreateForm && (
            <Button
              className="postif-card__new"
              variant="contained"
              color="primary"
              size="medium"
              onClick={handleNewForm}
              data-testid="postif-new"
            >
              + New PostIF
            </Button>
          )}
        </div>

        <PostIFTable
          variant={variant}
          rows={visibleRows}
          loading={loading}
          onEdit={(row) => navigate(withListSearch(`/hiring/postif/${row.id}/edit`))}
          onOpenDetail={setDetailRow}
          onOpen={handleOpenFromDetail}
          onDelete={setDeleteRow}
          onMarkAsEmployee={setMarkRow}
        />
      </section>

      <PostIFDetailDialog
        record={detailRow}
        openLabel={variant === "hr" ? "Open" : "Open form"}
        onClose={() => setDetailRow(null)}
        onOpen={handleOpenFromDetail}
      />

      <ConfirmationDialog
        open={Boolean(deleteRow)}
        title={POSTIF_TEXT.DELETE_CONFIRM_TITLE}
        message={deleteRow ? POSTIF_TEXT.DELETE_CONFIRM_MESSAGE(deleteRow.title) : ""}
        initialFocus="no"
        onClose={handleConfirmDelete}
      />

      <PostIFMarkEmployeeDialog
        open={Boolean(markRow)}
        applicantName={markRow?.applicant}
        defaultEmployeeId={markRow?.enterpriseEmployeeId}
        onClose={() => setMarkRow(null)}
        onConfirm={handleConfirmMark}
      />
    </div>
  );
};

export default PostInterviewForms;
